import axios, { AxiosRequestConfig } from 'axios';
import { VoiceAIConfig } from '../client';

export interface RequestOptions {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  data?: any;
  headers?: Record<string, string>;
  responseType?: 'json' | 'arraybuffer' | 'stream';
  config: VoiceAIConfig;
  modelTimeout?: number; // Model-specific timeout override
}

// Model-specific timeout settings (in milliseconds)
const MODEL_TIMEOUTS: Record<string, number> = {
  'orpheus': 90000,      // 90s - can be slow on cold start
  'orpheus-indic': 90000, // 90s - infrastructure cold start
  'xtts-v2': 90000,      // 90s - voice cloning takes time
  'mars6': 90000,        // 90s - complex processing
  'elevenlabs/v3': 60000, // 60s
  'whisper-v3': 120000,   // 120s - large audio files
  'llama-4-scout': 60000, // 60s - LLM generation
};

function getTimeout(url: string, config: VoiceAIConfig, modelTimeout?: number): number {
  // Priority: modelTimeout > model-specific > user config > default
  if (modelTimeout) return modelTimeout;
  
  // Check if URL matches a known model
  for (const [model, timeout] of Object.entries(MODEL_TIMEOUTS)) {
    if (url.includes(model.replace('/', '/'))) {
      return timeout;
    }
  }
  
  return config.timeout || 30000; // Default 30s
}

export async function makeRequest(options: RequestOptions): Promise<any> {
  const { url, method, data, headers, responseType = 'json', config, modelTimeout } = options;

  const timeout = getTimeout(url, config, modelTimeout);
  
  const axiosConfig: AxiosRequestConfig = {
    url,
    method,
    baseURL: config.baseUrl || 'https://api.slng.ai',
    headers: {
      'Authorization': `Bearer ${config.apiKey}`,
      'User-Agent': 'VoiceAI-SDK/0.1.2',
      ...headers,
    },
    data,
    responseType: responseType as any,
    timeout,
  };

  try {
    const response = await axios(axiosConfig);
    return response.data;
  } catch (error: any) {
    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
      const isKnownSlowModel = Object.keys(MODEL_TIMEOUTS).some(model => 
        options.url.includes(model.replace('/', '/'))
      );
      
      if (isKnownSlowModel) {
        throw new Error(
          `Request timed out after ${timeout/1000}s. Model may be starting up (cold start).\n` +
          `First calls can take 60-90 seconds. Please retry in a moment.\n` +
          `Tip: You can increase timeout with new VoiceAI({ timeout: 120000 })`
        );
      } else {
        throw new Error(
          `Request timed out after ${timeout/1000}s.\n` +
          `Try again or increase timeout with new VoiceAI({ timeout: 60000 })`
        );
      }
    }
    
    if (error.response) {
      const { status, data } = error.response;
      const message = data?.error || data?.message || 'Request failed';
      
      if (status === 401) {
        throw new Error(
          `Authentication failed: ${message}\n` +
          `Please check your API key. Get one at https://slng.ai/signup`
        );
      } else if (status === 402) {
        throw new Error(
          `Insufficient credits: ${message}\n` +
          `Add credits at https://slng.ai/dashboard`
        );
      } else if (status === 429) {
        throw new Error(
          `Rate limit exceeded: ${message}\n` +
          `Please slow down your requests or upgrade your plan at https://slng.ai/pricing`
        );
      } else if (status === 400) {
        throw new Error(`Invalid request: ${message}`);
      } else if (status >= 500) {
        throw new Error(
          `Server error (${status}): ${message}\n` +
          `The model service may be temporarily unavailable. Please retry.\n` +
          `If this persists, contact hello@slng.ai`
        );
      } else {
        throw new Error(
          `API Error (${status}): ${message}\n` +
          `Need help? Contact founders at hello@slng.ai`
        );
      }
    } else if (error.request) {
      throw new Error(
        'Network error: Unable to reach SLNG.AI servers.\n' +
        'Please check your internet connection.'
      );
    } else {
      throw new Error(`Unexpected error: ${error.message}`);
    }
  }
}